import { Calendar, Mail, MapPin, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

type SessionVideo = {
  vimeoId?: string;
  vimeoHash?: string;
};

type SessionRecording = { title: string; speakers?: string; videos: SessionVideo[] };

const getVimeoEmbedUrl = ({ vimeoId, vimeoHash }: SessionVideo) =>
  vimeoId
    ? `https://player.vimeo.com/video/${vimeoId}${vimeoHash ? `?h=${vimeoHash}` : ""}`
    : undefined;

const sessionRecordings: { day: string; sessions: SessionRecording[] }[] = [
  {
    day: "Day 1 — April 7",
    sessions: [
      {
        title: "Where AI is headed and what it means for energy",
        speakers: "Alex Epstein, Andrew Mayne, Rick Perry",
        videos: [
          { vimeoId: "1185283214", vimeoHash: "d54eae5c51" },
          { vimeoId: "1183070915", vimeoHash: "f9e0128854" },
          { vimeoId: "1183086123", vimeoHash: "3d673b4a07" },
        ],
      },
      {
        title: "Rapidly scaling electricity supply",
        speakers: "Alex Epstein, David Fotouhi",
        videos: [
          { vimeoId: "1186042034", vimeoHash: "facb882ef7" },
          { vimeoId: "1183220126", vimeoHash: "003f47d95a" },
        ],
      },
      {
        title: "Opening dinner: Richard Turner, legendary magician",
        videos: [{ vimeoId: "1184946694", vimeoHash: "d9ff8a09d2" }],
      },
    ],
  },
  {
    day: "Day 2 — April 8",
    sessions: [
      {
        title: "How to fix permitting",
        speakers: "Bruce Westerman, Mike Lee, Glenn Youngkin",
        videos: [
          { vimeoId: "1184868720", vimeoHash: "2af1fdf01d" },
          { vimeoId: "1183211068", vimeoHash: "514093881e" },
          { vimeoId: "1183206271", vimeoHash: "11aa764031" },
          { vimeoId: "1183203526", vimeoHash: "3d1de217f1" },
        ],
      },
      {
        title: "The future of US oil and gas",
        speakers: "Toby Rice, Kaes Van't Hof, Clay Gaspar",
        videos: [
          { vimeoId: "1184667785", vimeoHash: "d883a7c6e5" },
          { vimeoId: "1187122943", vimeoHash: "98c47a077b" },
          { vimeoId: "1183196604", vimeoHash: "49448e01da" },
        ],
      },
      {
        title: "Energizing the world: international opportunities and obstacles",
        speakers: "Mike Wirth, Jon McKenzie, GeFei Li",
        videos: [
          { vimeoId: "1183920856", vimeoHash: "31c5d01803" },
          { vimeoId: "1183162315", vimeoHash: "a0316e1a26" },
          { vimeoId: "1183170288", vimeoHash: "67b4ac0a06" },
          { vimeoId: "1183183529", vimeoHash: "083170baf2" },
        ],
      },
      {
        title: "Lunch: Energy Secretary Chris Wright",
        speakers: "Chris Wright",
        videos: [{ vimeoId: "1183095405", vimeoHash: "8b507f465a" }],
      },
      {
        title: "Unleashing nuclear energy",
        speakers: "David Wright, Nick Touran, Jacob DeWitte",
        videos: [
          { vimeoId: "1184832592", vimeoHash: "18d958e5d7" },
          { vimeoId: "1183188377", vimeoHash: "bdde44b47c" },
          { vimeoId: "1183179385", vimeoHash: "49a2a54f00" },
          { vimeoId: "1193633534", vimeoHash: "63c3ae1daf" },
        ],
      },
      {
        title: "Action leaders: people changing the world that everyone should know about",
        speakers: "Blake Scholl, Ethan Thornton",
        videos: [
          { vimeoId: "1184835599", vimeoHash: "236586a2c0" },
          { vimeoId: "1183125621", vimeoHash: "578179bef5" },
          { vimeoId: "1188176146", vimeoHash: "d351e1aa2e" },
        ],
      },
      {
        title: "Closing dinner",
        speakers: "Mike Rowe",
        videos: [{ vimeoId: "1187514038", vimeoHash: "82351c1771" }],
      },
    ],
  },
];

const Conference = () => (
  <div className="min-h-screen">
    <div className="container mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
      <section
        aria-labelledby="energy-freedom-2027-heading"
        className="grid items-stretch gap-8 lg:grid-cols-[minmax(0,1.55fr)_minmax(280px,0.85fr)] lg:gap-12"
      >
        <div className="flex min-w-0 flex-col">
          <h1
            id="energy-freedom-2027-heading"
            className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl"
          >
            Energy Freedom 2027
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-foreground sm:text-lg">
            Join us for the annual Energy Talking Points Executive Network conference.
          </p>

          <Card
            data-testid="conference-2027-details"
            className="mt-8 border-border/60 bg-gradient-to-br from-secondary/50 to-muted/30 shadow-sm"
          >
            <CardContent className="space-y-5 p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <Calendar aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                <p className="text-sm font-medium leading-5 text-foreground sm:text-base">March 30-31, 2027</p>
              </div>
              <div className="flex items-start gap-3">
                <MapPin aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                <p className="text-sm font-medium leading-5 text-foreground sm:text-base">
                  Pendry Newport Beach, California
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Users aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                <p className="text-sm font-medium leading-5 text-foreground sm:text-base">
                  200 ETP Members and Special Guests
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Mail aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                <a
                  href="mailto:michelle@alexepstein.com"
                  className="text-sm font-medium leading-5 text-foreground underline decoration-border underline-offset-4 transition-colors hover:text-primary sm:text-base"
                >
                  Email michelle@alexepstein.com to RSVP
                </a>
              </div>
            </CardContent>
          </Card>

          <div className="mt-10">
            <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              About the conference
            </h2>
            <p className="mt-5 max-w-2xl text-sm leading-7 text-foreground sm:text-base">
              Energy Freedom is the annual conference of Alex Epstein&apos;s Energy Talking Points Executive Network,
              where political, intellectual, and industry leaders collaborate on conquering America&apos;s greatest
              energy challenges.
            </p>
            <Link
              to="/conference/brochure"
              className="mt-5 inline-flex text-sm font-medium text-blue-600 underline underline-offset-4 transition-colors hover:text-blue-700 sm:text-base"
            >
              Click here to see the conference brochure
            </Link>
          </div>
        </div>

        <img
          src="/conference/pendry-pool.jpeg"
          alt="The pool at Pendry Newport Beach at dusk"
          data-testid="conference-2027-photo"
          className="aspect-[4/3] min-h-72 w-full rounded-md object-cover object-center shadow-sm lg:aspect-auto lg:h-full lg:min-h-[610px]"
        />
      </section>

      <Separator className="my-12 sm:my-16" />

      <section aria-labelledby="energy-freedom-2026-heading">
        <div className="mb-8">
          <h2
            id="energy-freedom-2026-heading"
            className="text-2xl font-semibold tracking-tight text-foreground sm:text-4xl"
          >
            Energy Freedom 2026
          </h2>
          <p className="mt-2 text-sm tracking-wide text-muted-foreground sm:text-base">
            April 7-8, 2026 in Newport Beach, California
          </p>
        </div>

        <div>
          <h2 className="mb-4 text-xl font-semibold text-foreground">Session Recordings</h2>
          <Separator className="mb-6" />

          <div className="space-y-10">
            {sessionRecordings.map((day) => (
              <div key={day.day}>
                <h3 className="mb-4 text-base font-semibold text-foreground">{day.day}</h3>
                <div className="space-y-6">
                  {day.sessions.map((session) => (
                    <Card key={session.title}>
                      <CardContent className="pt-6">
                        <h4 className="mb-1 text-base font-semibold text-foreground">{session.title}</h4>
                        {session.speakers && (
                          <p className="mb-4 text-sm text-muted-foreground">{session.speakers}</p>
                        )}
                        {session.videos.length > 0 ? (
                          <div className="space-y-6">
                            {session.videos.map((video, index) => {
                              const embedUrl = getVimeoEmbedUrl(video);

                              return (
                                <div
                                  key={`${session.title}-${video.vimeoId ?? "placeholder"}-${index}`}
                                  className="aspect-video w-full overflow-hidden rounded-lg bg-black sm:w-[70%]"
                                >
                                  {embedUrl ? (
                                    <iframe
                                      src={embedUrl}
                                      className="h-full w-full"
                                      frameBorder="0"
                                      allow="fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                                      allowFullScreen
                                      loading="lazy"
                                      title={`${session.title} recording ${index + 1}`}
                                    />
                                  ) : (
                                    <div className="flex h-full w-full items-center justify-center border border-border/60 bg-muted">
                                      <p className="text-sm text-muted-foreground">Upload coming soon</p>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="flex aspect-video w-full items-center justify-center rounded-lg border border-border/60 bg-muted sm:w-[70%]">
                            <p className="text-sm text-muted-foreground">Upload coming soon</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  </div>
);

export default Conference;
